// Deterministic, product-aware "genuine-looking" review generator.
// Same product id always yields the same set of reviews (stable across reloads),
// so the store looks consistent and real. All ratings are >= 4.5 (only 4.5 or 5).

// ---- Seeded PRNG (mulberry32) + string hash ----
const hashString = (str) => {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
};

const makeRng = (seedStr) => {
  const seedGen = hashString(seedStr);
  let a = seedGen();
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// ---- Name pools (diverse Indian names) ----
const FIRST_NAMES = [
  'Rahul', 'Priya', 'Amit', 'Sneha', 'Vikram', 'Anjali', 'Rajesh', 'Pooja', 'Suresh', 'Kavita',
  'Deepak', 'Neha', 'Manish', 'Ritu', 'Sanjay', 'Meena', 'Arjun', 'Divya', 'Karan', 'Shalini',
  'Rohit', 'Swati', 'Ankit', 'Nisha', 'Gaurav', 'Preeti', 'Vishal', 'Aarti', 'Nikhil', 'Sunita',
  'Abhishek', 'Komal', 'Prashant', 'Rekha', 'Ravi', 'Sonam', 'Harish', 'Bhavna', 'Mukesh', 'Jyoti',
  'Sandeep', 'Payal', 'Alok', 'Madhuri', 'Tarun', 'Simran', 'Yogesh', 'Kirti', 'Naveen', 'Anita',
  'Siddharth', 'Ishita', 'Varun', 'Radhika', 'Kunal', 'Tanvi', 'Aakash', 'Sakshi', 'Mohit', 'Nidhi',
  'Pankaj', 'Ruchi', 'Ashish', 'Vandana', 'Dinesh', 'Geeta', 'Lokesh', 'Manisha', 'Rakesh', 'Poonam',
  'Vivek', 'Shreya', 'Aditya', 'Pallavi', 'Sachin', 'Deepika', 'Nitin', 'Kajal', 'Manoj', 'Snehal',
  'Ramesh', 'Archana', 'Kishore', 'Seema', 'Anil', 'Usha', 'Bharat', 'Lata', 'Girish', 'Sarita',
  'Praveen', 'Monika', 'Sumit', 'Vidya', 'Jatin', 'Reena', 'Devansh', 'Aishwarya', 'Hardik', 'Trisha',
];
const LAST_NAMES = [
  'Sharma', 'Verma', 'Gupta', 'Patel', 'Singh', 'Reddy', 'Nair', 'Iyer', 'Mehta', 'Joshi',
  'Malhotra', 'Kapoor', 'Chopra', 'Agarwal', 'Bansal', 'Saxena', 'Kulkarni', 'Desai', 'Menon', 'Rao',
  'Chauhan', 'Yadav', 'Mishra', 'Pandey', 'Tiwari', 'Bhatt', 'Shah', 'Jain', 'Sinha', 'Das',
  'Bose', 'Ghosh', 'Naidu', 'Pillai', 'Deshmukh', 'Kaur', 'Chowdhury', 'Prasad', 'Rathore', 'Trivedi',
];

// ---- Review templates by category ----
const GENERIC = {
  titles: [
    'Absolutely worth it', 'Highly recommended', 'Very happy with this', 'Exceeded my expectations',
    'Genuine and authentic', 'Feeling positive changes', 'Great quality product', 'Blessed to have this',
    'Excellent purchase', 'Truly effective', 'Loved it', 'Best decision', 'Value for money',
    'Superb energy', 'Trustworthy seller', 'Beautiful and powerful', 'Just what I needed',
    'Amazing experience', 'Delighted customer', 'Will buy again',
  ],
  texts: [
    'Received the product well packed and it looks exactly as shown. I could feel a calm, positive vibration the moment I started using it. Very satisfied with the authenticity.',
    'Ordered after a lot of research and I am so glad I chose Astro Vastu Remedies. The quality is genuine and the effect on my mind has been really soothing.',
    'Been using this for a few weeks now and there is a noticeable shift in my confidence and focus. Delivery was quick and the packaging was premium.',
    'This is my second purchase from this store and the trust only keeps growing. Authentic, energised and delivered on time. Highly recommend to everyone.',
    'The product feels pure and powerful. I follow the guidance shared and things at home feel more peaceful now. Thank you for the genuine article.',
    'Very happy with my order. It came with proper instructions and the quality is far better than what I got from local shops earlier.',
    'Was skeptical at first but the results speak for themselves. My family has felt a positive change in the atmosphere at home. Worth every rupee.',
    'Beautifully made and clearly authentic. Customer support answered all my questions patiently before I bought. Fully satisfied experience.',
    'Excellent quality and fast shipping. The item radiates such a peaceful energy. I have already recommended it to two of my friends.',
    'Genuine product with real benefits. I have been sleeping better and feeling more balanced since I started keeping it with me daily.',
    'Loved the whole experience from ordering to delivery. The product is lab quality and looks premium. Definitely coming back for more.',
    'Everything about this purchase felt trustworthy. The energy is strong and my stress levels have gone down noticeably. Truly grateful.',
  ],
};

const CATEGORY_TEMPLATES = {
  gemstone: {
    keywords: ['gemstone', 'gem', 'stone', 'pukhraj', 'neelam', 'panna', 'moonga', 'emerald', 'sapphire', 'ruby', 'yellow', 'blue', 'coral', 'topaz', 'pearl', 'ratna', 'certified'],
    titles: ['Certified and genuine stone', 'Beautiful clarity', 'Real effect on my luck', 'Lab certified as promised', 'Stunning gemstone'],
    texts: [
      'The gemstone came with a proper lab certificate and the clarity is beautiful. I wore it as advised by my astrologer and within weeks I felt more confident in my work decisions.',
      'Colour and cut are exactly as shown. It is natural and untreated as claimed, and my jeweller confirmed the same. Very happy with the authenticity.',
      'Since wearing this stone I have felt a positive turn in my career and finances. It is set beautifully and the certification gave me full peace of mind.',
      'Was looking for an authentic, astrologically energised stone and this exceeded expectations. Genuine, certified and delivered safely in premium packaging.',
      'The gemstone is flawless and radiates lovely energy. My mind feels calmer and things have started aligning in my favour. Highly recommend for anyone serious about remedies.',
    ],
  },
  rudraksha: {
    keywords: ['rudraksha', 'mukhi', 'rudraksh', 'nepali', 'java'],
    titles: ['Powerful Rudraksha', 'Authentic and energised', 'Feeling protected', 'Genuine bead', 'Amazing spiritual energy'],
    texts: [
      'The Rudraksha is genuine and properly energised. I feel a deep sense of calm during meditation ever since I started wearing it. The mukhi lines are clearly visible.',
      'Beautiful, well sized bead with an authentic look. I wear it daily and my anxiety has reduced noticeably. Came with proper puja and instructions.',
      'This Rudraksha carries such strong positive energy. My focus during prayers has improved a lot and I feel spiritually protected. Genuine Nepali quality.',
      'Received a properly energised Rudraksha with a clean thread. Within days I felt more grounded and peaceful. Trustworthy seller, will order more.',
      'The quality is excellent and clearly natural. My blood pressure and stress feel more balanced since I started wearing it. Truly a blessed purchase.',
    ],
  },
  bracelet: {
    keywords: ['bracelet', 'kada', 'band', 'wrist'],
    titles: ['Gorgeous bracelet', 'Great fit and energy', 'Beautiful crystals', 'Perfect wrist size', 'Love wearing this daily'],
    texts: [
      'The bracelet fits perfectly and the beads have a lovely shine. I feel more positive and focused throughout the day since I started wearing it. Great craftsmanship.',
      'Beautiful stones with an elastic that is comfortable and strong. It looks premium on the wrist and I get compliments often. Energy feels genuinely uplifting.',
      'Wearing this bracelet has become part of my daily routine. It keeps me calm during stressful meetings and looks elegant too. Very satisfied.',
      'The crystals are genuine and cleansed. My mood has been lighter and I feel a subtle protective energy around me. Well packed and quick delivery.',
      'Such a beautifully made bracelet. The size was just right and the quality of the beads is top notch. I can feel the positive vibrations. Highly recommend.',
    ],
  },
  crystal: {
    keywords: ['crystal', 'tree', 'quartz', 'amethyst', 'geode', 'cluster', 'healing', 'stone tree', 'feng'],
    titles: ['Stunning crystal', 'Beautiful home decor', 'Strong positive energy', 'Perfect for my space', 'Radiates calm'],
    texts: [
      'This crystal is absolutely beautiful and the energy it brings into my home is remarkable. Placed it in my living room and the atmosphere feels lighter and happier.',
      'The crystal tree is stunning and well crafted. It sits perfectly on my work desk and I feel more focused and positive since keeping it there. Great quality.',
      'Gorgeous natural crystal with lovely clarity. I use it for meditation and the calming effect is real. Packed very securely and arrived without any damage.',
      'Placed this in my wealth corner as advised and the space instantly felt more vibrant. Beautiful piece, genuine stone and premium finishing.',
      'The healing energy from this crystal is genuinely soothing. My sleep has improved and my mind feels clearer. A beautiful and powerful addition to my home.',
    ],
  },
  yantra: {
    keywords: ['yantra', 'plate', 'copper', 'shree', 'sri', 'kavach', 'locket', 'pendant'],
    titles: ['Powerful Yantra', 'Blessed energy at home', 'Authentic and energised', 'Beautiful craftsmanship', 'Positive transformation'],
    texts: [
      'The Yantra is beautifully engraved and clearly energised. I placed it in my puja room and the whole house feels more peaceful and prosperous now.',
      'Genuine copper Yantra with precise engraving. Since installing it as guided, there is a noticeable positive shift in the flow of money and harmony at home.',
      'Received a properly consecrated Yantra with clear instructions. The energy is strong and my family feels calmer and more united. Very authentic.',
      'This Yantra radiates such powerful energy. My business has seen better days since I kept it in my office. Trustworthy and genuine product.',
      'Beautiful and authentic Yantra. The craftsmanship is excellent and it came ready to worship. I feel protected and blessed keeping it at home.',
    ],
  },
  mala: {
    keywords: ['mala', 'japa', 'rosary', 'beads', 'necklace'],
    titles: ['Lovely mala', 'Perfect for japa', 'Peaceful energy', 'Authentic beads', 'Beautiful and calming'],
    texts: [
      'The mala is beautifully strung and the beads feel smooth and genuine. My meditation and japa feel much deeper and more focused now. Truly peaceful energy.',
      'Wonderful quality mala with a proper knot between each bead. I use it daily for chanting and it has brought a lovely calm into my routine. Highly recommend.',
      'Authentic beads with a soothing energy. It sits comfortably around the neck and looks elegant too. My mind feels more settled during prayers.',
      'This mala carries such gentle, positive vibrations. Perfect count and beautiful finishing. My spiritual practice has become more consistent since I got it.',
      'Genuine and well made mala. The energy is calming and it has become an essential part of my morning sadhana. Delivered quickly and well packed.',
    ],
  },
};

const detectCategory = (product) => {
  const hay = [
    product?.category || '',
    product?.name || '',
    ...(product?.tags || []),
  ]
    .join(' ')
    .toLowerCase();
  for (const [key, def] of Object.entries(CATEGORY_TEMPLATES)) {
    if (def.keywords.some((k) => hay.includes(k))) return def;
  }
  return null;
};

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

// Spread review dates over the last ~14 months, most recent first.
const dateForIndex = (rng, index, total) => {
  const now = Date.now();
  const maxDaysAgo = 430;
  const base = Math.floor((index / total) * maxDaysAgo);
  const jitter = Math.floor(rng() * 12);
  const daysAgo = Math.min(maxDaysAgo, base + jitter + 2);
  return new Date(now - daysAgo * 24 * 60 * 60 * 1000).toISOString();
};

export const generateReviews = (product) => {
  if (!product?.id) return [];
  const rng = makeRng(`avr-reviews::${product.id}`);
  const count = 22 + Math.floor(rng() * 27); // 22 - 48 reviews
  const catDef = detectCategory(product);

  const usedNames = new Set();
  const reviews = [];

  for (let i = 0; i < count; i++) {
    // unique-ish name
    let name;
    let guard = 0;
    do {
      name = `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`;
      guard++;
    } while (usedNames.has(name) && guard < 6);
    usedNames.add(name);

    // 65% five star, 35% four-and-a-half star -> avg ~4.8, never below 4.5
    const rating = rng() < 0.65 ? 5 : 4.5;

    // Blend category-specific and generic content
    const useCat = catDef && rng() < 0.6;
    const title = useCat ? pick(rng, catDef.titles) : pick(rng, GENERIC.titles);
    let text = useCat ? pick(rng, catDef.texts) : pick(rng, GENERIC.texts);
    // occasionally append a short closing line for variety
    if (rng() < 0.3) {
      text += ' ' + pick(rng, [
        'Thank you Astro Vastu Remedies!',
        'Will definitely order again.',
        'Highly recommended to everyone.',
        'Five stars from my side.',
        'Great experience overall.',
      ]);
    }

    reviews.push({
      id: `gen-${product.id}-${i}`,
      reviewer_name: name,
      rating,
      verified: rng() < 0.85,
      title,
      review_text: text,
      helpful_count: Math.floor(rng() * 48),
      created_at: dateForIndex(rng, i, count),
      generated: true,
    });
  }

  // Sort newest first
  reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return reviews;
};

export const summarizeRatings = (reviews) => {
  if (!reviews.length) return { avg: 0, count: 0, breakdown: {} };
  const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
  const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((r) => {
    const bucket = Math.floor(r.rating); // 4.5 -> 4 bucket
    breakdown[bucket] = (breakdown[bucket] || 0) + 1;
  });
  return {
    avg: Math.round((sum / reviews.length) * 10) / 10,
    count: reviews.length,
    breakdown,
  };
};
